'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button, TextField, FieldHint } from 'ui';
import { useTranslation } from '@/i18n/client';
import { ContactCards } from '@/shared/components/contactCards';
import { useNotifications } from '@/shared/context/notifications-context';
import { emailPattern, isEmptyObject } from 'utils';
import { fetchBaselinkerOrderStatus } from '../../api/endpoints';

import styles from './TrackOrderPage.module.scss';
import { TrackForm } from './types';

export const TrackOrderPage = () => {
	const { t } = useTranslation(['trackOrder', 'common', 'payments', 'checkout']);
	const { showNotification } = useNotifications();
	const [isLoading, setIsLoading] = useState(false);
	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<TrackForm>();

	const onSubmit = async (data: TrackForm) => {
		setIsLoading(true);
		const baselinkerId = parseInt(data.orderNumber.replace(/\D/g, ''));

		const res = await fetchBaselinkerOrderStatus(data.email, baselinkerId);

		if (res.orderPage === null) {
			showNotification({
				id: 'get-baselinker-order-status-failed',
				type: 'error',
				message: t('trackOrder.getStatusFail'),
			});
			setIsLoading(false);

			return;
		}

		window.open(res.orderPage);
		setIsLoading(false);
	};

	return (
		<div className={styles.trackOrderPage}>
			<div className={styles['trackOrderPage--wrapper']}>
				<div className="container">
					<div className={styles['trackOrderPage__overflow-wrapper']}>
						<div className={styles.trackOrderPage__title}>
							<h1 className={styles['trackOrderPage__title--title']}>{t('trackOrder.title')}</h1>
							<p className={styles['trackOrderPage__title--description']}>
								{t('trackOrder.description')}
							</p>
						</div>
						<form className={styles.trackOrderPage__trackForm} onSubmit={handleSubmit(onSubmit)}>
							<TextField
								label={t('trackOrder.orderNumber')}
								{...register('orderNumber', {
									required: t('checkout:details.fieldRequired') || '',
									validate: (value) => {
										if (value.replace(/\D/g, '').length < 1) {
											return t('checkout:details.invalidFormat') || '';
										}
									},
								})}
								hasError={!!errors.orderNumber}
							/>
							{errors.orderNumber ? (
								<FieldHint isError>{errors.orderNumber?.message}</FieldHint>
							) : null}
							<TextField
								className={styles['trackOrderPage__trackForm--field']}
								label={t('trackOrder.orderEmail')}
								{...register('email', {
									required: t('checkout:details.fieldRequired') || '',
									pattern: {
										value: emailPattern,
										message: t('checkout:details.invalidEmail') || '',
									},
								})}
								hasError={!!errors.email}
							/>
							{errors.email ? <FieldHint isError>{errors.email?.message}</FieldHint> : null}
							<Button
								buttonName="checkStatus"
								variant="blue"
								type="submit"
								isLoading={isLoading}
								isDisabled={isLoading || !isEmptyObject(errors)}
								loaderSize={20}
								loaderStroke={2}
							>
								{t('trackOrder.checkStatus')}
							</Button>
						</form>
						<div className={styles['trackOrderPage__contact-wrapper']}>
							<h3>{t('common:common.cannotFindAnswer')}</h3>
							<h4>{t('payments:payments.contact')}</h4>
							<ContactCards showTitle={false} />
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};
